import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  Drawer,
  IconButton,
  TextField,
  CircularProgress,
} from "@mui/material";
import { useDispatch } from "react-redux";
import { IconX, IconPlus, IconTrash } from "@tabler/icons-react";

import {
  AdminUsers,
  GetCompanyAdmin,
  UpdateCompanyAdmin,
  SendAdminInvite,
} from "../../../services/jobService";
import { showAppAlert } from "../../../store/reducers/appreducer";
import { AdminPage, AdminTable } from "./AdminShell";
import tokens from "../../theme/tokens";

const { color, font, layout, radius, shadow } = tokens;

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const field = {
  "& .MuiOutlinedInput-root": { borderRadius: `${radius.control}px` },
  "& .MuiOutlinedInput-input": { padding: "12px 13px", fontSize: 13.5 },
};

const Label = ({ children }) => (
  <Box sx={{ mb: 0.75, font: `500 12px ${font.sans}`, color: color.textSecondary }}>
    {children}
  </Box>
);

const PanelShell = ({ open, onClose, title, children, footer }) => (
  <Drawer
    anchor="right"
    open={open}
    onClose={onClose}
    PaperProps={{
      sx: {
        width: layout.detailPanel,
        maxWidth: "100%",
        boxShadow: shadow.panel,
        borderLeft: `1px solid ${color.border}`,
      },
    }}
  >
    <Box sx={{ display: "flex", alignItems: "center", p: 2.75, pb: 2 }}>
      <Box sx={{ flex: 1, font: `600 15px ${font.sans}`, color: color.text }}>{title}</Box>
      <IconButton size="small" onClick={onClose}>
        <IconX size={16} stroke={1.75} color={color.textMuted} />
      </IconButton>
    </Box>
    <Box sx={{ flex: 1, overflowY: "auto", px: 2.75, pb: 2 }}>{children}</Box>
    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-end",
        gap: 1,
        px: 2.75,
        py: 2,
        borderTop: `1px solid ${color.border}`,
        bgcolor: color.bg,
      }}
    >
      {footer}
    </Box>
  </Drawer>
);

/**
 * Company Configuration: the company's own details, the people in it, and inviting more.
 * The old UI split this across a page plus two dialogs; it's one screen here.
 */
const CompanyUsers = () => {
  const dispatch = useDispatch();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  const [detailsOpen, setDetailsOpen] = useState(false);
  const [details, setDetails] = useState({ name: "", admin_email: "", phone: "" });
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [savingDetails, setSavingDetails] = useState(false);

  const [inviteOpen, setInviteOpen] = useState(false);
  const [emails, setEmails] = useState([""]);
  const [inviting, setInviting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await AdminUsers();
      setUsers(res.users || []);
    } catch (e) {
      dispatch(
        showAppAlert({ show: true, alertType: "error", alertMessage: e.message || "Failed to load users." })
      );
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    load();
  }, [load]);

  const openDetails = async () => {
    setDetailsOpen(true);
    setDetailsLoading(true);
    try {
      const res = await GetCompanyAdmin();
      if (res?.company) {
        setDetails({
          name: res.company.name || "",
          admin_email: res.company.admin_email || "",
          phone: res.company.phone || "",
        });
      }
    } catch (_) {
      /* leave the form blank; the user can still fill it in */
    } finally {
      setDetailsLoading(false);
    }
  };

  const saveDetails = async () => {
    if (!details.name.trim()) return;
    setSavingDetails(true);
    try {
      await UpdateCompanyAdmin(details);
      dispatch(
        showAppAlert({ show: true, alertType: "success", alertMessage: "Company details saved." })
      );
      setDetailsOpen(false);
    } catch (e) {
      dispatch(showAppAlert({ show: true, alertType: "error", alertMessage: e.message }));
    } finally {
      setSavingDetails(false);
    }
  };

  const sendInvites = async () => {
    const valid = emails.map((e) => e.trim()).filter((e) => e && EMAIL.test(e));
    if (!valid.length) return;

    setInviting(true);
    try {
      const res = await SendAdminInvite({ emails: valid });
      // The API reports per-address outcomes; "skipped" usually means already a member.
      const results = res?.results || [];
      const sent = results.filter((r) => r.status === "sent").length;
      const skipped = results.filter((r) => r.status === "skipped").length;

      dispatch(
        showAppAlert({
          show: true,
          alertType: sent ? "success" : "warning",
          alertMessage: sent
            ? `${sent} invite${sent === 1 ? "" : "s"} sent${skipped ? `, ${skipped} skipped` : ""}.`
            : "No invites were sent — those addresses may already be members.",
        })
      );
      setInviteOpen(false);
      setEmails([""]);
      load();
    } catch (e) {
      dispatch(showAppAlert({ show: true, alertType: "error", alertMessage: e.message }));
    } finally {
      setInviting(false);
    }
  };

  const anyValidEmail = emails.some((e) => EMAIL.test(e.trim()));

  return (
    <AdminPage
      title="Company configuration"
      subtitle="Your company's details and the people who can use the console."
      action={
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button variant="outlined" onClick={openDetails}>
            Company details
          </Button>
          <Button variant="contained" onClick={() => setInviteOpen(true)}>
            Invite people
          </Button>
        </Box>
      }
    >
      <AdminTable
        loading={loading}
        empty="No users in this company yet."
        rows={users}
        columns={[
          {
            key: "name",
            label: "Name",
            render: (u) => [u.first_name, u.last_name].filter(Boolean).join(" ") || "—",
          },
          { key: "email", label: "Email", width: "1.4fr" },
          { key: "phone", label: "Phone", width: "0.9fr" },
          {
            key: "role",
            label: "Role",
            width: "0.7fr",
            render: (u) => (u.is_admin ? "Admin" : u.role || "Member"),
          },
        ]}
      />

      <PanelShell
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        title="Company details"
        footer={
          <>
            <Button variant="outlined" onClick={() => setDetailsOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={saveDetails}
              disabled={savingDetails || !details.name.trim()}
            >
              {savingDetails ? "Saving…" : "Save"}
            </Button>
          </>
        }
      >
        {detailsLoading ? (
          <Box sx={{ display: "grid", placeItems: "center", py: 5 }}>
            <CircularProgress size={22} />
          </Box>
        ) : (
          <>
            <Label>Company name</Label>
            <TextField
              fullWidth
              size="small"
              value={details.name}
              onChange={(e) => setDetails((d) => ({ ...d, name: e.target.value }))}
              sx={{ ...field, mb: 2 }}
            />
            <Label>Admin email</Label>
            <TextField
              fullWidth
              size="small"
              value={details.admin_email}
              onChange={(e) => setDetails((d) => ({ ...d, admin_email: e.target.value }))}
              sx={{ ...field, mb: 2 }}
            />
            <Label>Phone</Label>
            <TextField
              fullWidth
              size="small"
              value={details.phone}
              onChange={(e) => setDetails((d) => ({ ...d, phone: e.target.value }))}
              sx={field}
            />
          </>
        )}
      </PanelShell>

      <PanelShell
        open={inviteOpen}
        onClose={() => setInviteOpen(false)}
        title="Invite people"
        footer={
          <>
            <Button variant="outlined" onClick={() => setInviteOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={sendInvites}
              disabled={inviting || !anyValidEmail}
            >
              {inviting ? "Sending…" : "Send invites"}
            </Button>
          </>
        }
      >
        <Box sx={{ font: `400 12px/1.6 ${font.sans}`, color: color.textMuted, mb: 2 }}>
          They'll get an email inviting them to join your company on Hire-Best.
        </Box>

        {emails.map((email, i) => {
          const invalid = email.trim() && !EMAIL.test(email.trim());
          return (
            <Box key={i} sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 1.25 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="name@company.com"
                value={email}
                onChange={(e) => {
                  const next = [...emails];
                  next[i] = e.target.value;
                  setEmails(next);
                }}
                error={Boolean(invalid)}
                helperText={invalid ? "Not a valid email address." : ""}
                sx={field}
              />
              {emails.length > 1 && (
                <IconButton
                  size="small"
                  onClick={() => setEmails(emails.filter((_, j) => j !== i))}
                  sx={{ mt: 0.5 }}
                >
                  <IconTrash size={15} stroke={1.75} color={color.textMuted} />
                </IconButton>
              )}
            </Box>
          );
        })}

        <Button
          onClick={() => setEmails([...emails, ""])}
          startIcon={<IconPlus size={14} stroke={2} />}
          sx={{ font: `500 12px ${font.sans}`, color: color.indigo }}
        >
          Add another
        </Button>
      </PanelShell>
    </AdminPage>
  );
};

export default CompanyUsers;
